import { useState } from 'react';
import {
  Alert,
  Button,
  Group,
  NumberInput,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';
import { Link, useParams } from 'react-router';
import { uygunsuzlukDurumEtiketi } from '@isg/validation';
import type { NonconformityDetail } from '@isg/shared-types';
import {
  AKSIYON_SUZGEC_BOS,
  useAksiyonAc,
  useAksiyonlar,
  useAtanabilirler,
  useOlayOptions,
  useUygunsuzluk,
  useUygunsuzlukGecmisi,
  useUygunsuzlukBes5,
  useUygunsuzlukOlayaTasi,
  useUygunsuzlukReddet,
} from '../api/queries';
import { DofSayaci } from './olay-dof-sayaci';
import { GundemineEkleDugmesi } from './kurul-parts';
import { QueryStatus } from './parts';
import { Bolum, CokenBolum, KapiSebebi, KayitDetayi, Rozet } from '../sozlesme';
import type { KapiDurumu } from '@isg/validation';
import { ANLAM } from '../theme';
import { tarihGoster, zamanGoster } from '../lib/format';

/**
 * UYGUNSUZLUK DETAYI — Tur 2½a (Kapsam §24.1/11, U11).
 *
 * ⚠️ **KAPANIŞ DÜĞMESİ YOKTUR** (§24.1/4): kayıt "kapalıdır" ⇔ DÖF ≥1 ∧ tümü
 * kapalı; kullanıcı bunu DÖF sayacından okur, kimse basmaz.
 * ⚠️ **DÜĞME GÖRÜNÜRLÜĞÜ SUNUCUDAN** (`yetkiler` — §2.40): ekran ne
 * değerlendirme listesini ne `uzman_aksiyon_atama` rejimini yeniden türetir;
 * görünmeyen düğmenin ucu da aynı kapıdan reddeder.
 * ⚠️ **ANONİM KAYITTA BİLDİREN GİZLİDİR** — sunucu zaten null döner; ekran
 * maskelemez (madde 37 dersi: ağdan inen yanıt da ayrımı taşımaz).
 */
export function UygunsuzlukDetayScreen(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const kayit = useUygunsuzluk(id);
  const gecmis = useUygunsuzlukGecmisi(id);

  if (!kayit.isSuccess) return <QueryStatus queries={[kayit]} kayit />;
  const k = kayit.data;
  return (
    <>
      <KayitDetayi
        testId="uygunsuzluk-detay"
        geriYol="/uygunsuzluklar"
        geriEtiket="Uygunsuzluklar"
        baslik={k.takipNo}
        ustBilgi={
          <Group gap="xs">
            <Rozet anlam={k.durum === 'rejected' ? 'engel' : 'bilgi'} testId="uygunsuzluk-durum">
              {uygunsuzlukDurumEtiketi(k.durum, k.dof)}
            </Rozet>
            <DofSayaci
              toplam={k.dof.toplam}
              acik={k.dof.acik}
              testId="uygunsuzluk-dof"
              bosCizilmez
            />
            {k.anonim && (
              <Rozet anlam="bilgi" testId="uygunsuzluk-anonim">
                Anonim
              </Rozet>
            )}
          </Group>
        }
        eylemler={
          <GundemineEkleDugmesi
            workplaceId={k.workplaceId}
            baslik={`Uygunsuzluk ${k.takipNo}`}
            kaynak={{ nonconformityId: k.id }}
            testId="uygunsuzluk-gundeme-ekle"
          />
        }
      >
        <Bolum baslik="Künye" testId="uygunsuzluk-kunye">
          <Table>
            <Table.Tbody>
              <Table.Tr>
                <Table.Td>İşyeri</Table.Td>
                <Table.Td>{k.workplaceAd}</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td>Lokasyon</Table.Td>
                <Table.Td>{k.locationAd ?? '—'}</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td>Tarih</Table.Td>
                <Table.Td>{tarihGoster(k.tarih)}</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td>Bildiren</Table.Td>
                <Table.Td data-testid="uygunsuzluk-bildiren">
                  {k.anonim ? 'Anonim' : (k.bildirenEtiket ?? '—')}
                </Table.Td>
              </Table.Tr>
              {k.tasinanIncidentTakipNo && (
                <Table.Tr>
                  <Table.Td>Taşındığı olay</Table.Td>
                  <Table.Td>
                    <Link
                      to={`/olaylar/${k.tasinanIncidentId}`}
                      data-testid="uygunsuzluk-tasinan-olay"
                    >
                      {k.tasinanIncidentTakipNo}
                    </Link>
                  </Table.Td>
                </Table.Tr>
              )}
              {/* Saha turu / S4 — kaynak tur TERS İLİŞKİDEN gelir (kolon yok);
                  fotoğraflar turun gözleminde durur, kopyalanmaz (§27.4). */}
              {k.kaynakSahaTuru && (
                <Table.Tr>
                  <Table.Td>Kaynak saha turu</Table.Td>
                  <Table.Td>
                    <Link
                      to={`/saha-turlari/${k.kaynakSahaTuru.id}`}
                      data-testid="uygunsuzluk-kaynak-saha-turu"
                    >
                      {k.kaynakSahaTuru.takipNo}
                    </Link>
                  </Table.Td>
                </Table.Tr>
              )}
              {k.kaynakIncidentTakipNo && (
                <Table.Tr>
                  <Table.Td>Kaynak olay</Table.Td>
                  <Table.Td>
                    <Link
                      to={`/olaylar/${k.kaynakIncidentId}`}
                      data-testid="uygunsuzluk-kaynak-olay"
                    >
                      {k.kaynakIncidentTakipNo}
                    </Link>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Bolum>

        <Bolum baslik="Ne görüldü?" testId="uygunsuzluk-metin">
          <Text data-testid="uygunsuzluk-aciklama-metni">{k.aciklama}</Text>
          {k.oneri && (
            <Text size="sm" c="dimmed" mt="xs" data-testid="uygunsuzluk-oneri-metni">
              Öneri: {k.oneri}
            </Text>
          )}
          {k.redGerekce && (
            <Alert color={ANLAM.engel} mt="xs" data-testid="uygunsuzluk-red-gerekce">
              Reddedildi: {k.redGerekce}
            </Alert>
          )}
        </Bolum>

        {/* U13 (tur 11 / R3-c) — 5x5 DEĞERLENDİRME. ⚠️ Bölüm yazılamaz
            durumda da GÖRÜNÜR ama SALT OKUNURDUR (§2.40 sıralaması): girilmiş
            bir puanı reddedilmiş kayıtta gizlemek, kullanıcıya "hiç
            puanlanmamış" izlenimi verirdi. */}
        {(k.yetkiler.bes5Yazabilir || k.puan5x5 !== null) && <Bes5Bolumu kayit={k} />}

        <DofBolumu kayitId={k.id} dofKapisi={k.yetkiler.dofKapisi} workplaceId={k.workplaceId} />

        {k.yetkiler.degerlendirebilir && <DegerlendirmeBolumu kayitId={k.id} />}
        {/* P13 · B1/5 — değerlendirme kapısı (tenant listesi → bahçe); liste SUNUCUDAN. */}
        {k.durum === 'reported' && (
          <KapiSebebi
            durum={k.yetkiler.degerlendirmeKapisi}
            nesne="Uygunsuzluğu"
            fiil="değerlendirir"
            testId="uygunsuzluk-degerlendirme-yazamama-sebebi"
          />
        )}

        <CokenBolum
          baslik="Geçiş geçmişi"
          sayi={(gecmis.data ?? []).length}
          ekleyebilir={false}
          testId="uygunsuzluk-gecmis"
        >
          <Table>
            <Table.Tbody>
              {(gecmis.data ?? []).map((g) => (
                <Table.Tr key={g.id}>
                  <Table.Td>{zamanGoster(g.createdAt)}</Table.Td>
                  <Table.Td>{g.yapanEtiket ?? 'Sistem'}</Table.Td>
                  <Table.Td>{g.gerekce ?? '—'}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </CokenBolum>
      </KayitDetayi>
    </>
  );
}

/** DÖF listesi + açma. Kaynak SUNUCUDA türetilir — ekran kaynak sormaz (T6). */
function DofBolumu({
  kayitId,
  dofKapisi,
  workplaceId,
}: {
  kayitId: string;
  /** P13 · B1/5 — `POST actions` adımları + kaydın durumu (bahçe dahil — eskiden yoktu). */
  dofKapisi: KapiDurumu;
  workplaceId: string;
}): React.JSX.Element {
  const yetkili = dofKapisi.yazabilir;
  const liste = useAksiyonlar({ ...AKSIYON_SUZGEC_BOS });
  const atanabilirler = useAtanabilirler({ enabled: yetkili });
  const ac = useAksiyonAc();
  const [acik, setAcik] = useState(false);
  const [baslik, setBaslik] = useState('');
  const [atanan, setAtanan] = useState<string | null>(null);
  const [termin, setTermin] = useState('');

  const dofler = (liste.data?.satirlar ?? []).filter((a) => a.nonconformityId === kayitId);

  return (
    <Bolum baslik="DÖF" testId="uygunsuzluk-dof-bolumu">
      {dofler.length === 0 ? (
        <Text size="sm" c="dimmed">
          Henüz DÖF açılmadı.
        </Text>
      ) : (
        <Table>
          <Table.Tbody>
            {dofler.map((a) => (
              <Table.Tr key={a.id}>
                <Table.Td>
                  <Link to={`/aksiyonlar/${a.id}`}>{a.baslik}</Link>
                </Table.Td>
                <Table.Td>{a.atananEtiket}</Table.Td>
                <Table.Td>{tarihGoster(a.terminTarihi)}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
      {yetkili && !acik && (
        <Button
          mt="xs"
          variant="light"
          data-testid="uygunsuzluk-dof-ac"
          onClick={() => setAcik(true)}
        >
          DÖF aç
        </Button>
      )}
      <KapiSebebi
        durum={dofKapisi}
        nesne="DÖF'ü"
        fiil="açar"
        testId="uygunsuzluk-dof-yazamama-sebebi"
      />
      {yetkili && acik && (
        <Stack mt="xs">
          {ac.error && (
            <Alert color={ANLAM.engel} role="alert" data-testid="uygunsuzluk-dof-hata">
              {ac.error.message}
            </Alert>
          )}
          <Textarea
            label="Başlık"
            data-testid="uygunsuzluk-dof-baslik"
            minRows={1}
            value={baslik}
            onChange={(e) => setBaslik(e.currentTarget.value)}
          />
          <Select
            label="Sorumlu"
            data-testid="uygunsuzluk-dof-atanan"
            value={atanan}
            onChange={setAtanan}
            searchable
            data={(atanabilirler.data ?? []).map((k) => ({ value: k.appUserId, label: k.etiket }))}
          />
          <TextInput
            type="date"
            label="Termin"
            data-testid="uygunsuzluk-dof-termin"
            value={termin}
            onChange={(e) => setTermin(e.currentTarget.value)}
          />
          <Group>
            <Button
              data-testid="uygunsuzluk-dof-kaydet"
              disabled={!baslik.trim() || !atanan || !termin || ac.isPending}
              onClick={() =>
                ac.mutate(
                  {
                    workplaceId,
                    baslik: baslik.trim(),
                    atananAppUserId: atanan!,
                    terminTarihi: termin,
                    nonconformityId: kayitId,
                  },
                  {
                    onSuccess: () => {
                      setAcik(false);
                      setBaslik('');
                    },
                  },
                )
              }
            >
              Aç
            </Button>
            <Button variant="default" onClick={() => setAcik(false)}>
              Vazgeç
            </Button>
          </Group>
        </Stack>
      )}
    </Bolum>
  );
}

/**
 * U13 — 5x5 DEĞERLENDİRME (tur 11 / R3-c; Kapsam §24.1/13).
 *
 * Olayın emekliye ayrılan değerlendirme akışından **taşındı**: aynı ölçek
 * (1-5 tam sayı), aynı türetme (puan = olasılık × şiddet), yeni ev.
 *
 * ⚠️ **SKOR BİR KAPI DEĞİLDİR.** Kapanış türetmesi (DÖF ≥1 ∧ tümü kapalı),
 * ret ve taşıma bu değerleri **hiç okumaz**; puansız uygunsuzluk tam olarak
 * puanlı uygunsuzluk kadar geçerlidir. Bölümün metni bunu **söyler** —
 * kullanıcı doldurmadığı bir alanın kendisini engellediğini sanmasın.
 * ⚠️ Yazılabilirlik SUNUCUDAN (`yetkiler.bes5Yazabilir`); ekran ikinci bir
 * kural yazmaz ve görünmeyen alanın ucu da 409 verir.
 */
function Bes5Bolumu({ kayit }: { kayit: NonconformityDetail }): React.JSX.Element {
  const bes5 = useUygunsuzlukBes5();
  const [olasilik, setOlasilik] = useState<number | string>(kayit.olasilik5 ?? 3);
  const [siddet, setSiddet] = useState<number | string>(kayit.siddet5 ?? 3);
  const yazilabilir = kayit.yetkiler.bes5Yazabilir;
  return (
    <Bolum
      baslik="Değerlendirme (5x5)"
      aciklama="Önceliklendirme içindir: puan hiçbir kapıyı açmaz veya kapatmaz, kayıt puansız da kapanabilir."
      testId="uygunsuzluk-bes5"
    >
      {bes5.error && (
        <Alert color={ANLAM.engel} role="alert" data-testid="uygunsuzluk-bes5-hata">
          {bes5.error.message}
        </Alert>
      )}
      {yazilabilir ? (
        <Group align="flex-end">
          <NumberInput
            label="Olasılık (1-5)"
            min={1}
            max={5}
            allowDecimal={false}
            value={olasilik}
            onChange={setOlasilik}
            data-testid="uygunsuzluk-bes5-olasilik"
          />
          <NumberInput
            label="Şiddet (1-5)"
            min={1}
            max={5}
            allowDecimal={false}
            value={siddet}
            onChange={setSiddet}
            data-testid="uygunsuzluk-bes5-siddet"
          />
          <Text size="sm" c="dimmed" data-testid="uygunsuzluk-bes5-puan">
            Puan (türetilir): {kayit.puan5x5 ?? '—'}
          </Text>
          <Button
            loading={bes5.isPending}
            data-testid="uygunsuzluk-bes5-kaydet"
            onClick={() =>
              bes5.mutate({
                id: kayit.id,
                input: { olasilik: Number(olasilik), siddet: Number(siddet) },
              })
            }
          >
            Kaydet
          </Button>
        </Group>
      ) : (
        <Group gap="lg">
          <Text size="sm">Olasılık: {kayit.olasilik5 ?? '—'}</Text>
          <Text size="sm">Şiddet: {kayit.siddet5 ?? '—'}</Text>
          <Text size="sm" fw={600} data-testid="uygunsuzluk-bes5-puan">
            Puan: {kayit.puan5x5 ?? '—'}
          </Text>
        </Group>
      )}
    </Bolum>
  );
}

/** U6 — değerlendirenin iki kararı: Reddet · Olaya taşı. */
function DegerlendirmeBolumu({ kayitId }: { kayitId: string }): React.JSX.Element {
  const options = useOlayOptions();
  const reddet = useUygunsuzlukReddet();
  const tasi = useUygunsuzlukOlayaTasi();
  const [gerekce, setGerekce] = useState('');
  const [turRef, setTurRef] = useState<string | null>(null);

  // ✏️ **TUR 11 / R3-a: TEHLİKELİ DURUM/DAVRANIŞ ARTIK LİSTELENMEZ.**
  // ~~Eski karar:~~ *"tüm türler seçilebilir; taşıma, olayın değerlendirme
  // akışına girişin tek yoludur."* O akış **emekliye ayrıldı** (§16.8/9
  // revizyonu) ve bu türlerle taşıma sunucuda **422** alır.
  //
  // ⚠️ **§2.40'IN SIRALAMASI: 1 ÇALIŞ · 2 GÖRÜNME · 3 HATA VER.** Seçeneği
  // listede bırakıp reddetmek en kötüsüdür — kullanıcı kendi hatası mı
  // sistem arızası mı ayırt edemez. İşaret SUNUCUDAN gelir
  // (`uygunsuzlukMu`); ekran kütüphane ref'i tanımaz.
  const turler = (options.data?.olayTurleri ?? []).filter((t) => !t.uygunsuzlukMu);

  return (
    <Bolum
      baslik="Değerlendirme"
      aciklama="DÖF açmak kabul demektir. Bulgu asılsız ya da mükerrerse reddedin; gerçekleşmiş bir olaysa Olaylar'a taşıyın."
      testId="uygunsuzluk-degerlendirme"
    >
      <Stack>
        {(reddet.error ?? tasi.error) && (
          <Alert color={ANLAM.engel} role="alert" data-testid="uygunsuzluk-karar-hata">
            {(reddet.error ?? tasi.error)?.message}
          </Alert>
        )}
        <Textarea
          label="Gerekçe"
          data-testid="uygunsuzluk-gerekce"
          minRows={2}
          value={gerekce}
          onChange={(e) => setGerekce(e.currentTarget.value)}
        />
        <Group>
          <Button
            variant="light"
            color={ANLAM.engel}
            data-testid="uygunsuzluk-reddet"
            disabled={!gerekce.trim() || reddet.isPending}
            onClick={() => reddet.mutate({ id: kayitId, gerekce: gerekce.trim() })}
          >
            Reddet
          </Button>
        </Group>
        <Select
          label="Olay türü"
          description="Taşıma için: hangi tür bir olay yaşandı?"
          data-testid="uygunsuzluk-tasima-tur"
          value={turRef}
          onChange={setTurRef}
          data={turler.map((t) => ({ value: t.id, label: t.ad }))}
        />
        <Group>
          <Button
            variant="light"
            data-testid="uygunsuzluk-olaya-tasi"
            disabled={!gerekce.trim() || tasi.isPending}
            onClick={() =>
              tasi.mutate({
                id: kayitId,
                input: { olayTuruRefId: turRef, gerekce: gerekce.trim() },
              })
            }
          >
            Olaya taşı
          </Button>
        </Group>
      </Stack>
    </Bolum>
  );
}
